/* ============================================================
   Lost Collective · Article TOC rail + mobile section nav
   File: assets/lc-article-toc.js

   Three navs from the same chapter list:
     - Desktop sticky TOC rail (.art-lrail) at left, with active
       section highlight via IntersectionObserver.
     - Mobile horizontal section nav (.art-mobile-nav) inserted
       at the top of the body.
     - Scrolly chapter rail (.lc-chapter-rail, LOS7-2659): a fixed
       left rail on scrollytelling pages only, text only, hidden at
       989px and below. Active state comes from the scrolly frame
       resolver's "lc:scrolly-chapter" event (lc-scrollytelling.js)
       for chapters that have frames, with the same h2 observer as
       the desktop rail covering chapters that have none.

   The first two skip on scrollytelling pages — the scrolly experience
   has its own pacing and the in-grid TOC competes with the sticky-image
   flow; the chapter rail is the scrolly-specific replacement.
   Waits on window.LCArticle.onReady so chapter h2s exist.
   ============================================================ */

(function () {
  'use strict';

  window.LCArticle = window.LCArticle || (function () {
    var state = { ready: false, queue: [] };
    return {
      onReady: function (fn) { if (state.ready) fn(); else state.queue.push(fn); },
      fireReady: function () {
        if (state.ready) return;
        state.ready = true;
        state.queue.forEach(function (fn) { try { fn(); } catch (_e) {} });
        state.queue.length = 0;
      }
    };
  })();

  function ready(fn) {
    if (document.readyState !== 'loading') return fn();
    document.addEventListener('DOMContentLoaded', fn);
  }

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function el(tag, attrs, html) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === 'class') n.className = attrs[k];
      else n.setAttribute(k, attrs[k]);
    });
    if (html != null) n.innerHTML = html;
    return n;
  }

  function slugOf(text) {
    return String(text || '').trim().toLowerCase()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function ensureIds(sections) {
    sections.forEach(function (h2, i) {
      if (h2.id) return;
      var titleEl = h2.querySelector('.ttl');
      var slug = titleEl ? slugOf(titleEl.textContent) : 'section-' + (i + 1);
      h2.id = 'sec-' + slug;
    });
  }

  function titleOf(h2, i) {
    var titleEl = h2.querySelector('.ttl');
    return titleEl ? titleEl.textContent.trim() : 'Section ' + (i + 1);
  }

  function findH2ForAnchor(sections, anchor) {
    for (var i = 0; i < sections.length; i++) {
      var h2 = sections[i];
      var ttl = h2.querySelector('.ttl');
      var slug = ttl ? slugOf(ttl.textContent) : '';
      if (slug === anchor || h2.id === anchor || h2.id === 'sec-' + anchor) return h2;
    }
    return null;
  }

  // Build the chapter list <ol> shared by the desktop rail and the scrolly
  // rail: number + title link per h2, smooth-scroll on click.
  function buildChapterList(sections) {
    var ol = el('ol');
    sections.forEach(function (h2, i) {
      var li = el('li', { 'data-target': h2.id });
      li.appendChild(el('span', { class: 'num' }, pad(i + 1)));
      li.appendChild(el('a', { href: '#' + h2.id }, titleOf(h2, i)));
      ol.appendChild(li);
    });
    ol.querySelectorAll('a[href^="#"]').forEach(function (a) {
      a.addEventListener('click', function (ev) {
        ev.preventDefault();
        var target = document.getElementById(a.getAttribute('href').slice(1));
        if (target) {
          var prefersReduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          target.scrollIntoView({ behavior: prefersReduce ? 'auto' : 'smooth', block: 'start' });
          history.replaceState(null, '', '#' + target.id);
        }
      });
    });
    return ol;
  }

  // Active-item highlight shared by both rails. The h2 observer marks a
  // chapter as its heading passes the upper part of the viewport; on
  // scrolly pages the frame resolver's chapter event overrides it while a
  // chapter's frames are in play.
  function wireActive(rail, sections, listenScrolly) {
    var items = rail.querySelectorAll('li');
    var byId = {};
    items.forEach(function (li) { byId[li.getAttribute('data-target')] = li; });
    function indexOfId(id) {
      for (var i = 0; i < sections.length; i++) if (sections[i].id === id) return i;
      return -1;
    }
    function activeIndex() {
      var li = rail.querySelector('li.is-active');
      return li ? indexOfId(li.getAttribute('data-target')) : -1;
    }
    function activate(id) {
      var li = byId[id];
      if (!li || li.classList.contains('is-active')) return;
      items.forEach(function (other) { other.classList.remove('is-active'); });
      li.classList.add('is-active');
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          activate(entry.target.id);
        });
      }, { rootMargin: '-10% 0px -50% 0px', threshold: 0 });
      sections.forEach(function (h2) { io.observe(h2); });
    }
    if (listenScrolly) {
      // The resolver keeps announcing the last chapter whose frames it
      // resolved, so below the body it pulled the active item back to "03 One
      // day" (LOS7-3032). A backwards move is honoured only while the reader
      // is still inside that chapter's block, i.e. the next chapter's heading
      // has not yet reached the middle of the viewport.
      document.addEventListener('lc:scrolly-chapter', function (ev) {
        var anchor = ev && ev.detail ? ev.detail.anchor : '';
        var h2 = anchor ? findH2ForAnchor(sections, anchor) : null;
        if (!h2) return;
        var target = indexOfId(h2.id);
        if (target < activeIndex()) {
          var next = sections[target + 1];
          var vh = window.innerHeight || document.documentElement.clientHeight;
          if (next && next.getBoundingClientRect().top < vh * 0.5) return;
        }
        activate(h2.id);
      });
    }
  }

  function buildDesktopRail(root, sections) {
    var rail = el('nav', { class: 'art-lrail', 'aria-label': 'Article sections' });
    rail.appendChild(el('span', { class: 'lrail-hd' }, 'Sections'));
    rail.appendChild(buildChapterList(sections));
    root.insertBefore(rail, root.firstChild);
    wireActive(rail, sections, false);
  }

  // Scrolly chapter rail (LOS7-2659). Fixed to the left edge of the viewport,
  // outside the article grid (scrolly pages run data-rail="off"), so it
  // never offsets content; CSS hides it at 989px and below. Text only.
  function buildScrollyRail(sections) {
    if (document.querySelector('.lc-chapter-rail')) return;
    var rail = el('nav', { class: 'lc-chapter-rail', 'aria-label': 'Chapters' });
    rail.appendChild(el('span', { class: 'lc-chapter-rail__hd' }, 'Chapters'));
    rail.appendChild(buildChapterList(sections));
    document.body.appendChild(rail);
    wireActive(rail, sections, true);

    // The rail shows only while it sits over paper prose (LOS7-3032). It
    // hides (1) above the first chapter heading, (2) whenever its box meets a
    // figure, a scrolly block, the contact sheet or a prints module, and (3)
    // once the end of the body is above 40% of the viewport. At 1440 and
    // below it sat over every full-width figure and ran on into the index,
    // the prints and the references.
    var first = sections[0];
    var body = document.getElementById('articleBody');
    var ticking = false;
    var blocked = new Set();

    // (2) without layout reads on scroll: an IntersectionObserver whose root
    // margin shrinks the viewport to the rail's own box plus a 24px buffer,
    // so "intersecting" means "overlaps the rail". Rebuilt on resize, since
    // the rail's box changes across the 990 and 1280 tiers.
    var OBSTACLES = 'figure.fig, .lc-scrolly, .lc-contact, .photo-index, .lc-article-prints';
    var BUFFER = 24;
    var io = null;
    function observeObstacles() {
      if (!io) return;
      document.querySelectorAll(OBSTACLES).forEach(function (o) { io.observe(o); });
    }
    // lc-scrollytelling.css is deferred (media=print swap), so this can run
    // before the rail is fixed to the left edge: measured on live, the first
    // build saw a static rail at the foot of the page and set a root margin
    // of -44606px, and the rail then sat over every scrolly. Build only once
    // the rail is really fixed; until then, retry briefly.
    var retries = 0;
    function buildObserver() {
      if (!('IntersectionObserver' in window)) return;
      if (io) io.disconnect();
      io = null;
      blocked.clear();
      if (window.getComputedStyle(rail).position !== 'fixed') {
        if (retries++ < 40) setTimeout(buildObserver, 250);
        return;
      }
      var r = rail.getBoundingClientRect();
      if (!r.width || !r.height) return;
      var vw = document.documentElement.clientWidth;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var m = [
        -Math.max(0, r.top - BUFFER),
        -Math.max(0, vw - r.right - BUFFER),
        -Math.max(0, vh - r.bottom - BUFFER),
        -Math.max(0, r.left - BUFFER),
      ].map(function (v) { return Math.round(v) + 'px'; }).join(' ');
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) blocked.add(en.target);
          else blocked.delete(en.target);
        });
        onScroll();
      }, { rootMargin: m, threshold: 0 });
      observeObstacles();
    }

    function check() {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var show = first.getBoundingClientRect().top < vh * 0.6 &&
        blocked.size === 0 &&
        !(body && body.getBoundingClientRect().bottom < vh * 0.4);
      if (rail.classList.contains('is-visible') === show) return;
      rail.classList.toggle('is-visible', show);
      if (show) rail.removeAttribute('aria-hidden');
      else rail.setAttribute('aria-hidden', 'true');
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(check);
    }
    var resizeTimer = 0;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      onScroll();
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(buildObserver, 150);
    }, { passive: true });
    rail.setAttribute('aria-hidden', 'true');
    buildObserver();
    // The contact sheet and the prints interstitial can land after this
    // runs; pick them up once the page has settled.
    window.addEventListener('load', function () {
      retries = 0;
      buildObserver();
    });
    setTimeout(observeObstacles, 2000);
    check();
  }

  function buildMobileNav(root, sections) {
    var nav = el('nav', { class: 'art-mobile-nav', 'aria-label': 'Article sections' });
    var ol = el('ol');
    sections.forEach(function (h2, i) {
      if (!h2.id) return;
      var li = el('li');
      var a = el('a', { href: '#' + h2.id }, titleOf(h2, i));
      a.addEventListener('click', function (ev) {
        ev.preventDefault();
        var prefersReduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        h2.scrollIntoView({ behavior: prefersReduce ? 'auto' : 'smooth', block: 'start' });
        history.replaceState(null, '', '#' + h2.id);
      });
      li.appendChild(a);
      ol.appendChild(li);
    });
    nav.appendChild(ol);

    var firstSection = sections[0];
    if (firstSection && firstSection.parentNode === root) {
      root.insertBefore(nav, firstSection);
    } else {
      root.insertBefore(nav, root.childNodes[1] || root.firstChild);
    }
  }

  function init() {
    var root = document.getElementById('articleBody');
    if (!root) return;

    var sections = Array.prototype.slice.call(root.querySelectorAll('h2.h2-mark'));
    if (!sections.length) return;
    ensureIds(sections);

    var hasScrolly = !!document.querySelector('.lc-scrolly');

    // Scrolly pages get no in-grid TOC and no mobile nav. The chapter rail
    // (LOS7-2659) is opt-in per article: sections/main-article.liquid sets
    // data-chapter-rail="true" on <article> from the "chapter-rail" tag or
    // the custom.chapter_rail metafield. Two chapters is enough to list.
    if (hasScrolly) {
      var articleEl = root.closest('article');
      var wantsRail = !!(articleEl && articleEl.getAttribute('data-chapter-rail') === 'true');
      if (wantsRail && sections.length >= 2) buildScrollyRail(sections);
      return;
    }

    // Desktop rail: skip when there are fewer than 3 sections (the rail
    // looks bare with 1-2 entries).
    if (sections.length >= 3) {
      buildDesktopRail(root, sections);
    }

    // Mobile nav: render whenever there are 2+ sections.
    if (sections.length >= 2) {
      buildMobileNav(root, sections);
    }
  }

  ready(function () {
    window.LCArticle.onReady(init);
  });
})();
